import axios from "axios";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import CheckboxInput from "~/shared/form/CheckboxInput";
import ControlledForm from "~/shared/form/ControlledForm";
import FieldContext from "~/shared/form/FieldContext";
import FormButton from "~/shared/form/FormButton";
import FormIconButton from "~/shared/form/FormIconButton";
import FormResponse from "~/shared/form/FormResponse";
import HiddenInput from "~/shared/form/HiddenInput";
import TextInput from "~/shared/form/TextInput";

export default function ManageAvailability() {
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const [editing, setEditing] = useState(false);
    const formHook = useForm({ defaultValues, disabled: !editing });
    const { watch, formState: { errors } } = formHook;
    const fieldArrays = [];
    daysOfWeek.forEach((_, i) => fieldArrays.push(useFieldArray({ control: formHook.control, name: `availability.${i}.times` })));

    useEffect(() => {
        fieldArrays.forEach(({ fields, append }) => {
            if (fields.length === 0) {
                append({});
            }
        });
    });

    async function defaultValues() {
        const data = (await axios.post("staff/load-profile")).data;
        data.availability.sort((a, b) => daysOfWeek.findIndex(day => day === a.day) - daysOfWeek.findIndex(day => day === b.day));

        daysOfWeek.forEach((day, i) => {
            if (data.availability[i]?.day !== daysOfWeek[i]) {
                data.availability.splice(i, 0, { day, times: [] });
            }
        });

        fieldArrays.forEach((_, i) => {
            if (data.availability.some(a => a.day === daysOfWeek[i]) && data.availability[i]?.times.length > 0) {
                data.availability[i].available = true;
            }
        });

        return data;
    }

    function isValidTimeRange(start, end) {
        if (!start || !end) {
            return true;
        }

        return new Date(`1/1/1 ${start}`) < new Date(`1/1/1 ${end}`);
    }

    function onSubmit(data) {
        data.availability = data.availability.filter(d => d?.available).map(({ day, times }) => ({ day, times }));

        if (data.availability.length === 0) {
            formHook.setError("root.formResponse", { type: "error", message: "You must select at least one day you are available." });
            return false;
        }
    }

    function onSuccess(_, __, data) {
        setEditing(false);

        daysOfWeek.forEach((day, i) => {
            if (data.availability[i]?.day !== daysOfWeek[i]) {
                data.availability.splice(i, 0, { day, times: [] });
            }
        });

        formHook.reset(data);
        formHook.reset();

        fieldArrays.forEach((_, i) => {
            if (data.availability.some(a => a.day === daysOfWeek[i]) && data.availability[i]?.times.length > 0) {
                formHook.setValue(`availability.${i}.available`, true);
            }
        });
    }

    function resetForm() {
        setEditing(false);
        formHook.reset();

        const availability = formHook.getValues("availability");
        fieldArrays.forEach((_, i) => {
            if (availability.some(a => a.day === daysOfWeek[i]) && availability[i]?.times.length > 0) {
                formHook.setValue(`availability.${i}.available`, true);
            }
        });
    }

    return (
        <section>
            <ControlledForm hook={formHook} onSubmit={onSubmit} post="staff/update-profile" onSuccess={onSuccess}>
                <div className="d-flex flex-column align-items-center gap-2 mb-3">
                    {daysOfWeek.map((day, i) => (
                        <div key={day} className="border rounded shadow-sm p-3 w-fit">
                            <FieldContext id={`availability.${i}`}>
                                <HiddenInput id="day" value={daysOfWeek[i]} />
                                <div className="d-flex gap-3 align-items-start flex-wrap">
                                    <div className="d-flex gap-2 align-items-center mt-2">
                                        <CheckboxInput id="available" noMargin deps={fieldArrays[i].fields.map((_, j) => [`availability.${i}.times.${j}.start`, `availability.${i}.times.${j}.end`]).flat()} />
                                        <span className="fw-bold fs-5 text-start" style={{ width: "8rem" }}>{day}</span>
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        {fieldArrays[i].fields.map((field, j) => (
                                            <FieldContext id={`times.${j}`} key={field.id} disabled={!watch(`availability.${i}.available`)}>
                                                <div className="d-flex flex-column">
                                                    <div className="d-flex gap-2 align-items-center flex-wrap flex-sm-nowrap">
                                                        <TextInput label="Start Time" id="start" validate={(start, { availability }) => isValidTimeRange(start, availability[i].times[j].end)} deps={`availability.${i}.times.${j}.end`} type="time" minWidth={10} maxWidth={10} hideLabel hideValidationMessage noMargin required />
                                                        <span>-</span>
                                                        <TextInput label="End Time" id="end" validate={(end, { availability }) => isValidTimeRange(availability[i].times[j].start, end)} deps={`availability.${i}.times.${j}.start`} type="time" minWidth={10} maxWidth={10} hideLabel hideValidationMessage noMargin required />
                                                        {editing && (j === 0 ? (
                                                            <FormIconButton legacy icon="bi-plus-lg" onClick={() => fieldArrays[i].append({})} />
                                                        ) : (
                                                            <FormIconButton legacy icon="bi-x-lg" onClick={() => fieldArrays[i].remove(j)} />
                                                        ))}
                                                    </div>
                                                    {errors.availability?.[i]?.times?.[j]?.start?.type === "validate" && (
                                                        <Form.Control.Feedback type="invalid" className="d-block" style={{ marginTop: "-2px" }}>End time must be after start time.</Form.Control.Feedback>
                                                    )}
                                                </div>
                                            </FieldContext>
                                        ))}
                                    </div>
                                </div>
                            </FieldContext>
                        </div>
                    ))}
                </div>
                <FormResponse />
                {editing ? <div className="btn-container max-w-md">
                    <FormButton legacy submit>Save</FormButton>
                    <FormButton legacy onClick={resetForm}>Cancel</FormButton>
                </div> : (
                    <FormButton legacy onClick={() => setEditing(true)}>Edit</FormButton>
                )}
            </ControlledForm>
        </section>
    );
}